import { useMemo, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { createSelector } from 'reselect';
import axios from 'axios';
import auth from '../../../config/utils/auth';
import { uuidv4 } from '../../../config/utils/helpers';

function postEditControlLock(payload, state, errorLabel) {
  const token = auth.getToken();
  if (!token || !payload) return;
  axios.post('/api/v1/productionline/editControlLocks/', { ...payload, state }, { headers: { Authorization: `Bearer ${token}` } }).catch((error) => {
    console.error(errorLabel, error);
  });
}

export function buildEditControlDescriptor(action, data) {
  const resolvedAction = action ?? '';
  const value = data && typeof data === 'object' ? data.id : data; // use id when payload is an object (queue row/modal data), raw data when primitive
  const hasValue = value !== undefined && value !== null && value !== '';

  return {
    action: resolvedAction,
    editKey: hasValue ? `${resolvedAction}|${String(value)}` : resolvedAction
  };
}

export function useEditControlPresence({ action, data, manageLock = false, isOpen = true, enabled = true } = {}) {
  const lockPayloadRef = useRef(null);

  const descriptor = useMemo(() => buildEditControlDescriptor(action, data), [action, data]);
  const selectActiveCount = useMemo(
    () =>
      createSelector(
        (state) => state.websocket.editControlLocks,
        (editControlLocks) => {
          if (!enabled) return 0;
          const seen = new Set();
          (editControlLocks || []).forEach((entry) => {
            if (entry.state === 'close' || entry.state === 'closed' || entry.active === false || entry.editKey !== descriptor.editKey) return;
            seen.add(entry.lockId);
          });
          return seen.size;
        }
      ),
    [enabled, descriptor.editKey]
  );
  const activeCount = useSelector(selectActiveCount);

  useEffect(() => {
    if (!enabled || !manageLock || !isOpen || !action) {
      return;
    }

    if (lockPayloadRef.current) {
      return;
    }

    const payload = {
      lockId: uuidv4(),
      action: descriptor.action,
      editKey: descriptor.editKey
    };

    lockPayloadRef.current = payload;
    postEditControlLock(payload, 'open', 'Failed to open edit lock');
  }, [descriptor.action, descriptor.editKey, manageLock, isOpen, action, enabled]);

  useEffect(() => {
    if (!enabled || !manageLock || !isOpen || !action) {
      return;
    }

    const interval = setInterval(() => {
      const payload = lockPayloadRef.current;
      postEditControlLock(payload, 'open', 'Failed to refresh edit lock');
    }, 10000); // 10 sec heartbeat for lock lifetime - ttl on redis key is 20 sec

    return () => clearInterval(interval);
  }, [manageLock, isOpen, action, enabled]);

  useEffect(() => {
    return () => {
      const payload = lockPayloadRef.current;
      if (!payload) {
        return;
      }

      postEditControlLock(payload, 'close', 'Failed to close edit lock');
      lockPayloadRef.current = null;
    };
  }, []);

  return {
    activeCount,
    descriptor
  };
}
